import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import QuoteForm from '../../components/feature/QuoteForm';

const HomePage = () => {
  useEffect(() => {
    document.title = 'NWA Roofing | Storm Damage Repair & Free Inspections | Fayetteville, Rogers, Bentonville';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'NWA Roofing connects Northwest Arkansas homeowners with expert storm damage repair, free roof inspections, and insurance claim assistance. Serving Fayetteville, Springdale, Rogers, Bentonville, and Bella Vista with 25+ years of roofing excellence. Emergency roof repair, hail damage restoration, and complete roof replacement services available 24/7.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'NWA roofing, Fayetteville roofing, storm damage repair, roof inspection, Northwest Arkansas roofer, hail damage roof repair, emergency roof repair, roof replacement, insurance claim assistance, residential roofing');
    }

    // Add FAQ Schema
    const faqSchema = document.createElement('script');
    faqSchema.type = 'application/ld+json';
    faqSchema.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Do you offer free roof inspections in Northwest Arkansas?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, we connect you with certified roofing contractors who provide completely free roof inspections throughout Fayetteville, Springdale, Rogers, Bentonville, and Bella Vista. Our partner contractors will assess your roof condition, identify any storm damage or hail damage, and provide a detailed estimate at no cost."
          }
        },
        {
          "@type": "Question",
          "name": "How quickly can you respond to emergency roof repairs?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Our network of roofing contractors offers 24/7 emergency roof repair services throughout Northwest Arkansas. Most contractors can respond within 2-4 hours for urgent situations like active leaks or storm damage to prevent further damage to your property."
          }
        },
        {
          "@type": "Question",
          "name": "Do you help with insurance claims for roof damage?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Absolutely. Our partner contractors specialize in insurance claim assistance and work directly with insurance adjusters to maximize your coverage. They handle all documentation, photos, and communication with your insurance company to make the process seamless for hail damage, wind damage, and storm damage claims."
          }
        },
        {
          "@type": "Question",
          "name": "What areas do you serve in Northwest Arkansas?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We connect homeowners with expert roofing contractors throughout Northwest Arkansas, including Fayetteville, Springdale, Rogers, Bentonville, Bella Vista, Siloam Springs, Lowell, Cave Springs, Centerton, and Pea Ridge. All contractors are local, licensed, and insured."
          }
        },
        {
          "@type": "Question",
          "name": "What types of roofing services do you offer?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Our network provides comprehensive roofing services including storm damage repair, hail damage restoration, emergency roof repairs, complete roof replacement, roof inspections, insurance claim assistance, asphalt shingle installation, metal roofing, commercial roofing, and preventive roof maintenance for both residential and commercial properties."
          }
        }
      ]
    });
    document.head.appendChild(faqSchema);

    return () => {
      if (faqSchema.parentNode) {
        faqSchema.parentNode.removeChild(faqSchema);
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-flashlight-line',
      title: 'Storm Damage Repair',
      description: 'Expert hail damage roof repair and wind damage restoration for Northwest Arkansas homes. Emergency storm damage repair with fast response to protect your property from further damage.',
      link: '/services/roof-repair'
    },
    {
      icon: 'ri-search-eye-line',
      title: 'Free Roof Inspections',
      description: 'Comprehensive roof damage assessment and free roof inspection near me. Professional roof damage inspection to identify issues before they become expensive problems.',
      link: '/services/roof-inspection'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Insurance Claims Assistance',
      description: 'Navigate insurance claim roof processes with confidence. We work directly with insurance adjusters to maximize your hail damage insurance claim coverage and roof claim assistance.',
      link: '/services/insurance-adjuster'
    },
    {
      icon: 'ri-home-gear-line',
      title: 'Complete Roof Replacement',
      description: 'Premium architectural asphalt roof shingles and 3 tab asphalt roof shingles installed by certified professionals. Durable asphalt laminated shingles protection for NWA weather conditions with competitive roof replacement cost.',
      link: '/services/roof-replacement'
    },
    {
      icon: 'ri-tools-line',
      title: 'Commercial Roofing',
      description: 'Long-lasting metal roofing, standing seam metal roof, and flat roof repair installations. Energy-efficient roofing solutions for commercial properties and modern homes.',
      link: '/services/commercial-roofing'
    },
    {
      icon: 'ri-alarm-warning-line',
      title: 'Emergency Roof Repairs',
      description: '24/7 emergency roof repair and roof leak repair near me services. Immediate response to storm damage roof repair near me to prevent water damage and protect your home.',
      link: '/services/emergency-roof-repair'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Mitchell',
      location: 'Fayetteville, AR',
      rating: 5,
      text: 'After the hail storm last spring, NWA Roofing came out within 24 hours for a free inspection. They handled everything with our insurance company and had our new roof installed in just three days. The crew was professional, cleaned up perfectly, and the roof looks amazing. Highly recommend for anyone in Northwest Arkansas needing storm damage repair!'
    },
    {
      name: 'Michael Thompson',
      location: 'Bentonville, AR',
      rating: 5,
      text: 'We needed a complete roof replacement and NWA Roofing exceeded our expectations. Their team explained every step of the process, helped us choose the perfect architectural shingles, and completed the job on time and within budget. The quality of workmanship is outstanding. Best roofing company in NWA!'
    },
    {
      name: 'Jennifer Rodriguez',
      location: 'Rogers, AR',
      rating: 5,
      text: 'I called NWA Roofing for an emergency leak repair during a storm. They arrived quickly, fixed the problem immediately, and followed up with a comprehensive inspection. Their honesty and professionalism are unmatched. They could have sold me a new roof, but instead repaired what was needed. That is integrity!'
    }
  ];

  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Hero Section - Mobile Optimized */}
      <header className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16 sm:pt-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/9a4d12d1af910798fa97a5fa95168955.jpg"
            alt="Professional NWA roofing installation in Fayetteville Arkansas showing quality architectural shingles"
            title="Expert roofing contractors Northwest Arkansas"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-12 sm:py-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="text-white text-center lg:text-left">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-4 sm:mb-6 leading-tight">
                NWA ROOFING | STORM DAMAGE EXPERTS
              </h1>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl mb-6 sm:mb-8 font-light leading-relaxed">
                <strong>Free Roof Inspections</strong> • <strong>Hail Damage Insurance Claims</strong> • <strong>Emergency Roof Repair Near Me</strong> • Serving Fayetteville, Springdale, Rogers &amp; Bentonville
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 max-w-lg mx-auto lg:mx-0">
                <a
                  href="tel:4793455312"
                  className="bg-white text-blue-900 px-6 sm:px-8 py-3.5 sm:py-4 rounded-lg font-bold text-base sm:text-lg hover:bg-blue-50 transition-all shadow-xl flex items-center justify-center whitespace-nowrap touch-manipulation active:scale-95"
                  aria-label="Call NWA Roofing for emergency roof repair"
                >
                  <i className="ri-phone-fill mr-2"></i>
                  Call (479) 345-5312
                </a>
                <a
                  href="#quote-form"
                  className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-lg font-bold text-base sm:text-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-xl flex items-center justify-center whitespace-nowrap touch-manipulation active:scale-95"
                  aria-label="Get free roof inspection in Northwest Arkansas"
                >
                  Get Free Inspection
                </a>
              </div>
            </div>

            <div className="hidden lg:block">
              <QuoteForm variant="compact" />
            </div>
          </div>
        </div>
      </header>

      {/* Trust Signals Bar - Mobile Optimized */}
      <section className="bg-gray-50 py-6 sm:py-8 border-y border-gray-200" aria-label="Trust indicators">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6 text-center">
            <div className="flex flex-col items-center">
              <i className="ri-time-line text-3xl sm:text-4xl text-blue-600 mb-2" aria-hidden="true"></i>
              <p className="font-semibold text-gray-900 text-xs sm:text-sm lg:text-base">25+ Years Experience</p>
            </div>
            <div className="flex flex-col items-center">
              <i className="ri-shield-check-line text-3xl sm:text-4xl text-blue-600 mb-2" aria-hidden="true"></i>
              <p className="font-semibold text-gray-900 text-xs sm:text-sm lg:text-base">Licensed &amp; Insured</p>
            </div>
            <div className="flex flex-col items-center">
              <i className="ri-star-line text-3xl sm:text-4xl text-blue-600 mb-2" aria-hidden="true"></i>
              <p className="font-semibold text-gray-900 text-xs sm:text-sm lg:text-base">A+ BBB Rating</p>
            </div>
            <div className="flex flex-col items-center">
              <i className="ri-award-line text-3xl sm:text-4xl text-blue-600 mb-2" aria-hidden="true"></i>
              <p className="font-semibold text-gray-900 text-xs sm:text-sm lg:text-base">Lifetime Warranty</p>
            </div>
            <div className="flex flex-col items-center col-span-2 sm:col-span-1">
              <i className="ri-clipboard-line text-3xl sm:text-4xl text-blue-600 mb-2" aria-hidden="true"></i>
              <p className="font-semibold text-gray-900 text-xs sm:text-sm lg:text-base">Free Inspections</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid Section - Mobile Optimized */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-12 lg:mb-16">
            <p className="text-blue-600 font-semibold uppercase tracking-wider text-xs sm:text-sm mb-2 sm:mb-3">OUR ROOFING SERVICES</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 px-4">
              Complete Roofing Solutions for NWA Homeowners
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              From <strong>storm damage restoration near me</strong> to complete <strong>residential roof replacement</strong>, we provide comprehensive <strong>roofing services</strong> throughout Northwest Arkansas including <strong>roof inspection services near me</strong>, <strong>insurance claim roof assistance</strong>, and <strong>emergency storm damage repair</strong> with unmatched quality and customer service.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {services.map((service, index) => (
              <article
                key={index}
                className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 group touch-manipulation"
              >
                <Link to={service.link} className="block">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-blue-100 rounded-full mb-4 sm:mb-6 group-hover:bg-blue-600 transition-colors">
                    <i className={`${service.icon} text-2xl sm:text-3xl text-blue-600 group-hover:text-white transition-colors`} aria-hidden="true"></i>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">{service.title}</h3>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-3 sm:mb-4">{service.description}</p>
                  <span className="text-blue-600 font-semibold flex items-center group-hover:text-blue-700 text-sm sm:text-base">
                    Learn More <i className="ri-arrow-right-line ml-2" aria-hidden="true"></i>
                  </span>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section - Mobile Optimized */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-blue-50 to-blue-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://readdy.ai/api/search-image?query=detailed%20close-up%20view%20of%20professional%20roofer%20installing%20high%20quality%20architectural%20asphalt%20shingles%20on%20residential%20roof%2C%20hands%20working%20with%20roofing%20materials%20and%20tools%2C%20showing%20expert%20craftsmanship%20and%20precision%20installation%20technique%2C%20natural%20lighting%20highlighting%20texture%20of%20premium%20shingles%2C%20no%20vehicles%20or%20branding%20visible%20in%20frame&width=800&height=600&seq=nwa-roofing-closeup-work&orientation=landscape"
                alt="Professional roofing contractor installing architectural shingles in Northwest Arkansas"
                title="Expert roof installation Fayetteville AR"
                className="rounded-2xl sm:rounded-3xl shadow-2xl w-full"
              />
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-orange-600 font-semibold uppercase tracking-wider text-xs sm:text-sm mb-2 sm:mb-3">WHY CHOOSE OUR NETWORK</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
                Connect with Northwest Arkansas's Most Trusted Roofing Contractors
              </h2>
              <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed mb-4 sm:mb-6">
                We connect homeowners throughout Fayetteville, Springdale, Rogers, Bentonville, and Bella Vista with vetted <strong>roofing contractors near me</strong> who specialize in <strong>storm damage repair</strong>, <strong>hail damage roof repair</strong>, <strong>insurance claims assistance</strong>, and complete <strong>roof replacement</strong> services with competitive <strong>roof replacement cost</strong> estimates.
              </p>
              <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed mb-4 sm:mb-6">
                Our network of certified <strong>roofing professionals</strong> understands the unique weather challenges in Northwest Arkansas. From severe <strong>hail storms</strong> to high winds causing <strong>wind damage roof</strong> issues, our partner contractors have the expertise to assess damage through comprehensive <strong>roof damage inspection</strong>, work with your <strong>insurance adjuster</strong>, and restore your roof to peak condition with quality <strong>asphalt shingles</strong>, <strong>architectural shingles</strong>, or <strong>metal roofing</strong> solutions.
              </p>
              <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed mb-6 sm:mb-8">
                We pride ourselves on connecting you with contractors who offer transparent pricing, quality workmanship, and exceptional customer service. Every project includes a comprehensive <strong>free roof inspection</strong>, detailed estimate, and <strong>lifetime warranty</strong> options. When you use our service to find <strong>residential roofing</strong> contractors, you're getting access to the <strong>best rated roofing companies</strong> in the area.
              </p>

              <div className="grid grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
                <div className="text-center">
                  <p className="text-3xl sm:text-4xl lg:text-5xl font-bold text-orange-600 mb-1 sm:mb-2">100%</p>
                  <p className="text-gray-700 font-medium text-xs sm:text-sm lg:text-base">Free Service</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl sm:text-4xl lg:text-5xl font-bold text-orange-600 mb-1 sm:mb-2">24/7</p>
                  <p className="text-gray-700 font-medium text-xs sm:text-sm lg:text-base">Quick Response</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl sm:text-4xl lg:text-5xl font-bold text-orange-600 mb-1 sm:mb-2">Local</p>
                  <p className="text-gray-700 font-medium text-xs sm:text-sm lg:text-base">NWA Contractors</p>
                </div>
              </div>

              <Link
                to="/about"
                className="inline-flex items-center border-2 border-orange-600 text-orange-600 px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-bold hover:bg-orange-600 hover:text-white transition-all whitespace-nowrap text-sm sm:text-base touch-manipulation active:scale-95"
                rel="nofollow"
              >
                Learn More About Our Service <i className="ri-arrow-right-line ml-2" aria-hidden="true"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Mobile Optimized */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-4">
              What NWA Homeowners Say
            </h2>
          </div>

          <div className="relative">
            <div className="bg-blue-50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 shadow-xl">
              <div className="flex items-center justify-center mb-4 sm:mb-6">
                <div className="bg-gray-900 text-white px-4 sm:px-6 py-2 rounded-full flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-orange-400 text-base sm:text-xl" aria-hidden="true"></i>
                  ))}
                  <span className="ml-2 sm:ml-3 font-bold text-sm sm:text-base">5.0</span>
                </div>
              </div>

              <blockquote className="text-base sm:text-lg lg:text-2xl text-gray-800 leading-relaxed mb-6 sm:mb-8 text-center font-serif">
                "{testimonials[currentTestimonial].text}"
              </blockquote>

              <div className="flex items-center justify-center">
                <div className="text-center">
                  <p className="font-bold text-base sm:text-lg text-gray-900">{testimonials[currentTestimonial].name}</p>
                  <p className="text-gray-600 text-sm sm:text-base">{testimonials[currentTestimonial].location}</p>
                </div>
              </div>

              <nav className="flex justify-center items-center mt-6 sm:mt-8 space-x-3 sm:space-x-4" aria-label="Testimonial navigation">
                <button
                  onClick={() => setCurrentTestimonial((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1))}
                  className="min-w-[44px] min-h-[44px] sm:w-12 sm:h-12 flex items-center justify-center border-2 border-gray-300 rounded-lg hover:border-blue-600 hover:text-blue-600 transition-colors cursor-pointer touch-manipulation active:scale-95"
                  aria-label="Previous testimonial"
                >
                  <i className="ri-arrow-left-line text-lg sm:text-xl" aria-hidden="true"></i>
                </button>
                <div className="flex space-x-2">
                  {testimonials.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentTestimonial(index)}
                      className={`min-w-[44px] min-h-[44px] sm:w-3 sm:h-3 rounded-full transition-colors cursor-pointer touch-manipulation ${
                        index === currentTestimonial ? 'bg-blue-600' : 'bg-gray-300'
                      }`}
                      aria-label={`Go to testimonial ${index + 1}`}
                      aria-current={index === currentTestimonial ? 'true' : 'false'}
                    ></button>
                  ))}
                </div>
                <button
                  onClick={() => setCurrentTestimonial((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1))}
                  className="min-w-[44px] min-h-[44px] sm:w-12 sm:h-12 flex items-center justify-center bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer touch-manipulation active:scale-95"
                  aria-label="Next testimonial"
                >
                  <i className="ri-arrow-right-line text-lg sm:text-xl" aria-hidden="true"></i>
                </button>
              </nav>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas Section - Mobile Optimized */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
              Proudly Serving Northwest Arkansas
            </h2>
            <p className="text-sm sm:text-base lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              We provide expert <strong>roofing services near me</strong> throughout NWA, including <strong>free roof inspections</strong>, <strong>storm damage repair</strong>, <strong>roof leak repair near me</strong>, and complete <strong>roof replacement</strong> for residential and commercial properties. Our <strong>local roofing companies</strong> serve all major cities in the region.
            </p>
          </div>

          <nav className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6" aria-label="Service areas">
            {['Fayetteville', 'Springdale', 'Rogers', 'Bentonville', 'Bella Vista', 'Siloam Springs', 'Lowell', 'Cave Springs', 'Centerton', 'Pea Ridge'].map((city, index) => (
              <div key={index} className="bg-white rounded-xl p-4 sm:p-6 text-center shadow-md hover:shadow-xl transition-shadow touch-manipulation">
                <i className="ri-map-pin-line text-3xl sm:text-4xl text-blue-600 mb-2 sm:mb-3" aria-hidden="true"></i>
                <h3 className="font-bold text-gray-900 text-sm sm:text-base">{city}</h3>
              </div>
            ))}
          </nav>
        </div>
      </section>

      {/* CTA Section - Mobile Optimized */}
      <section id="quote-form" className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-blue-600 to-blue-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 items-center">
            <div className="text-white text-center lg:text-left">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold mb-4 sm:mb-6 leading-tight">
                Ready to Protect Your Home with a New Roof?
              </h2>
              <p className="text-base sm:text-lg lg:text-xl mb-6 sm:mb-8 leading-relaxed">
                Get your <strong>free roof inspection</strong> today! Our certified <strong>roofing experts</strong> will assess your roof condition, identify any <strong>storm damage</strong> or <strong>hail damage</strong>, and provide a detailed estimate at no cost. We work with all major insurance companies to make the <strong>insurance claim roof</strong> process seamless and maximize your coverage.
              </p>
              <ul className="space-y-3 sm:space-y-4">
                <li className="flex items-start sm:items-center">
                  <i className="ri-checkbox-circle-fill text-2xl sm:text-3xl text-green-400 mr-3 sm:mr-4 flex-shrink-0 mt-1 sm:mt-0" aria-hidden="true"></i>
                  <span className="text-sm sm:text-base lg:text-lg">Free comprehensive roof damage inspection</span>
                </li>
                <li className="flex items-start sm:items-center">
                  <i className="ri-checkbox-circle-fill text-2xl sm:text-3xl text-green-400 mr-3 sm:mr-4 flex-shrink-0 mt-1 sm:mt-0" aria-hidden="true"></i>
                  <span className="text-sm sm:text-base lg:text-lg">Insurance claim assistance and adjuster coordination</span>
                </li>
                <li className="flex items-start sm:items-center">
                  <i className="ri-checkbox-circle-fill text-2xl sm:text-3xl text-green-400 mr-3 sm:mr-4 flex-shrink-0 mt-1 sm:mt-0" aria-hidden="true"></i>
                  <span className="text-sm sm:text-base lg:text-lg">Lifetime warranty options on all installations</span>
                </li>
                <li className="flex items-start sm:items-center">
                  <i className="ri-checkbox-circle-fill text-2xl sm:text-3xl text-green-400 mr-3 sm:mr-4 flex-shrink-0 mt-1 sm:mt-0" aria-hidden="true"></i>
                  <span className="text-sm sm:text-base lg:text-lg">24/7 emergency roof repair service</span>
                </li>
              </ul>
            </div>

            <div>
              <QuoteForm />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
